import React from "react";
import {useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {Formik} from "formik";
import {AdminLayoutFullHeightAndTwoCells} from "../../atoms/admin-layout";
import {DocumentTitle} from "../../atoms/document-title";
import {
  Button,
  TextField,
  SelectField,
  FormCard,
  Text,
  FlexRow,
  theme,
  TextAreaField,
} from "@reside/ui";
import {Spinner} from "../../atoms/spinner";
import {UpdateSsoConfigurationRequest} from "../../services/SsoService";
import {useSsoConfigurationQuery, useUpdateSsoConfiguration} from "./hooks";
import {SSO_CONFIGURATION_VALIDATION_SCHEMA} from "./validator";
import {IDP_CLIENT_OPTIONS, IDP_CLIENT_FIELD_LABELS} from "./constants";
import {BackButton} from "./BackButton";
import {select} from "../../store";
import {RoleNameEnum} from "../../utils/types/common";

const EDIT_SSO_CONFIGURATION_PAGE_TITLE =
  "Reside - Admin - Edit SSO Configuration";

export const PageAdminSsoConfigurationEdit = () => {
  const {organizationId} = useParams<{organizationId: string}>();
  const {data: existingConfig, isLoading: isLoadingConfig} =
    useSsoConfigurationQuery();
  const {mutate: updateConfiguration, isLoading: isUpdating} =
    useUpdateSsoConfiguration();

  const userRole = useSelector(select.adminSession.userRoleName);
  const isOrganizationItAdmin = userRole === RoleNameEnum.ORGANIZATION_IT_ADMIN;

  const handleSubmit = (values: UpdateSsoConfigurationRequest) => {
    updateConfiguration(values);
  };

  const renderPageContent = () => {
    if (isLoadingConfig) {
      return <Spinner />;
    }

    if (!existingConfig) {
      return <Text>No SSO configuration found to edit.</Text>;
    }

    const initialValues: UpdateSsoConfigurationRequest = {
      idpClient: existingConfig.idpClient,
      certificate: existingConfig.certificate,
      issuerUrl: existingConfig.issuerUrl,
      samlEndpoint: existingConfig.samlEndpoint,
      sloEndpoint: existingConfig.sloEndpoint,
    };

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={values => SSO_CONFIGURATION_VALIDATION_SCHEMA(values)}
      >
        {({submitForm, values}) => {
          const fieldLabels =
            IDP_CLIENT_FIELD_LABELS[
              values.idpClient as keyof typeof IDP_CLIENT_FIELD_LABELS
            ] ?? IDP_CLIENT_FIELD_LABELS.onelogin;
          return (
            <>
              {!isOrganizationItAdmin && (
                <BackButton
                  to={`/admin/organizations/${organizationId}/edit`}
                  label="Edit Organization"
                />
              )}
              <FormCard title="SSO Configuration">
                <Text as="p" fontSize={[3]} style={{marginBottom: "32px"}}>
                  Enter and submit the details below to configure SSO
                </Text>
                <SelectField
                  label="IDP CLIENT"
                  name="idpClient"
                  options={IDP_CLIENT_OPTIONS}
                  getOptionLabel={option => option.label}
                  getOptionValue={option => option.value}
                  disabled
                />
                <TextAreaField label="x.509 certificate" name="certificate" />
                <TextField label={fieldLabels.issuerUrl} name="issuerUrl" />
                <TextField
                  label={fieldLabels.samlEndpoint}
                  name="samlEndpoint"
                />
                <TextField label={fieldLabels.sloEndpoint} name="sloEndpoint" />
                <FlexRow
                  style={{
                    justifyContent: "flex-start",
                    gap: theme.space[5],
                    marginTop: theme.space[7],
                  }}
                >
                  <Button
                    onClick={submitForm}
                    color="primary"
                    disabled={isUpdating}
                  >
                    {isUpdating ? "Saving..." : "Submit"}
                  </Button>
                </FlexRow>
              </FormCard>
            </>
          );
        }}
      </Formik>
    );
  };

  return (
    <AdminLayoutFullHeightAndTwoCells>
      <DocumentTitle title={EDIT_SSO_CONFIGURATION_PAGE_TITLE} />

      {!isOrganizationItAdmin && (
        <BackButton
          to={`/admin/organizations/${organizationId}/edit`}
          label="Edit Organization"
        />
      )}

      {renderPageContent()}
    </AdminLayoutFullHeightAndTwoCells>
  );
};
